/* app/login-spice.js — login console enhancer (shared: main + UI Pass).
   Plain JS, no React. DOM corrections to the shared login:

     1. Replace the retired 6-stage rail with a widget for PYLON — the
        swappable build-engine layer (PY series). One job, one swappable
        engine on the wire (legacy · router · codex · claude); Codex CLI is
        the live engine, the TE router the maturing main engine. Whatever is
        mounted runs through ONE universal supervisor → ONE shared dev-grounded
        QA gate → ONE engine-agnostic contract (manifest · events · index.html).
        Grounded in PY-OVERVIEW-pylon-swappable-engine-layer.md.
     2. Replace the floaty, wrongly-scoped chips with STATIC product-wide
        proof tiles.

   The React originals stay (hidden by CSS); a MutationObserver keeps our nodes
   present across login re-mounts. */

(function () {
  // 13px line-icons (stroke = currentColor)
  const ICO = {
    pylon:   '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7h16M9 7v3a3 3 0 0 0 6 0V7"/><path d="M12 13v5"/><path d="M8 21h8"/></svg>',
    codex:   '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="m7 8-4 4 4 4"/><path d="m13 9-2 6"/><path d="M20 7v10"/></svg>',
    router:  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1.6" fill="currentColor"/></svg>',
    claude:  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v18M5.6 5.6l12.8 12.8M3 12h18M5.6 18.4 18.4 5.6"/></svg>',
    legacy:  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="5" rx="1"/><path d="M5 9v9a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9"/><path d="M10 13h4"/></svg>',
    super:   '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3 4 6v5c0 5 3.5 8 8 10 4.5-2 8-5 8-10V6z"/></svg>',
    gate:    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.2-3.2M8.5 11l2 2 3.5-3.5"/></svg>',
    contract:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M7 3h7l5 5v13H7z"/><path d="M14 3v5h5"/><path d="M10 14h6M10 17h4"/></svg>',
    source:  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 8V5a1 1 0 0 1 1-1h3M16 4h3a1 1 0 0 1 1 1v3M20 16v3a1 1 0 0 1-1 1h-3M8 20H5a1 1 0 0 1-1-1v-3"/><rect x="9" y="9" width="6" height="6" rx="1"/></svg>',
    send:    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M22 2 11 13"/><path d="M22 2 15 22l-4-9-9-4z"/></svg>',
  };

  // ⚙️ The TE router is officially named — change this ONE constant on any future rename.
  const TE_ROUTER_NAME = 'Bullseye';

  // the swappable engines on the wire (flat enum: legacy · router · codex · claude)
  const ENGINES = [
    { id: 'codex',  ico: 'codex',  name: 'Codex CLI',     st: 'live · agentic', cls: 'is-live' },
    { id: 'router', ico: 'router', name: TE_ROUTER_NAME,  st: 'maturing',       cls: 'is-warm' },
    { id: 'claude', ico: 'claude', name: 'Claude',        st: 'queued',         cls: 'is-off'  },
    { id: 'legacy', ico: 'legacy', name: 'Legacy',        st: 'archived',       cls: 'is-off'  },
  ];

  // the shared spine every engine flows through (engine-agnostic)
  const SPINE = [
    { ico: 'super',    b: 'Supervisor', s: '$0 · cancel-safe', active: true },
    { ico: 'gate',     b: 'QA gate',    s: 'dev-grounded' },
    { ico: 'contract', b: 'Contract',   s: 'manifest · events' },
  ];

  function buildPylon() {
    const el = document.createElement('div');
    el.className = 'pylon';
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML =
      '<div class="pylon-top">' +
        '<span class="pylon-mark">' + ICO.pylon + '</span>' +
        '<span class="pylon-name">Pylon</span>' +
        '<span class="pylon-sub">swappable build engine</span>' +
        '<span class="pylon-tag">engine-agnostic</span>' +
      '</div>' +
      '<div class="pylon-bay">' +
        ENGINES.map(e =>
          '<div class="eng ' + e.cls + '">' +
            '<div class="eng-row">' +
              '<span class="eng-ico">' + ICO[e.ico] + '</span>' +
              '<span class="eng-name">' + e.name + '</span>' +
              '<span class="eng-dot"></span>' +
            '</div>' +
            '<span class="eng-st">' + e.st + '</span>' +
          '</div>'
        ).join('') +
      '</div>' +
      '<div class="pylon-spine">' +
        // the mounted engine feeds the shared spine
        '<div class="sp active"><span class="sp-dot"></span><span class="sp-txt"><b>Codex</b><span>codex exec</span></span></div>' +
        SPINE.map((n, i) =>
          '<span class="sp-link l' + (i + 1) + '"></span>' +
          '<div class="sp' + (n.active ? ' active' : '') + '"><span class="sp-dot"></span>' +
            '<span class="sp-txt"><b>' + n.b + '</b><span>' + n.s + '</span></span></div>'
        ).join('') +
      '</div>';
    return el;
  }

  function ensure() {
    // the shared subtitle still references the retired "six-stage" rail — correct it
    const sub = document.querySelector('.login-sub');
    if (sub && /six-stage/i.test(sub.textContent)) {
      sub.textContent = sub.textContent.replace(/six-stage squadron/i, 'agent squadron');
    }
    const console_ = document.querySelector('.login-stage .console');
    if (console_ && !console_.querySelector('.pylon')) {
      const log = console_.querySelector('.logwrap');
      const pylon = buildPylon();
      if (log) console_.insertBefore(pylon, log);
      else console_.appendChild(pylon);
    }
  }

  const mo = new MutationObserver(() => ensure());
  function start() { mo.observe(document.body, { childList: true, subtree: true }); ensure(); }
  if (document.body) start();
  else document.addEventListener('DOMContentLoaded', start);
})();
